import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../lib/api.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import type { SecurityProfile } from '@mayksel/types';

function useApiOpts() {
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  return { getToken, workspaceId };
}

export function useSecurityProfiles() {
  const { getToken, workspaceId } = useApiOpts();
  return useQuery<SecurityProfile[]>({
    queryKey: ['security-profiles', workspaceId],
    queryFn: async () => {
      const token = await getToken();
      return api.get<SecurityProfile[]>('/v1/security-profiles', {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId,
  });
}

export interface ProfileInput {
  name: string;
  description?: string | undefined;
  isDefault?: boolean | undefined;
  fieldRules?: Array<{ entityType: string; fieldKey: string; isVisible: boolean; isEditable: boolean }>;
  sectionRules?: Array<{ entityType: string; section: string; isVisible: boolean }>;
  actionRules?: Array<{ action: string; isAllowed: boolean }>;
}

export function useCreateSecurityProfile() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ProfileInput) => {
      const token = await getToken();
      return api.post<SecurityProfile>('/v1/security-profiles', input, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['security-profiles', workspaceId] });
    },
  });
}

export function useUpdateSecurityProfile() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: ProfileInput }) => {
      const token = await getToken();
      return api.patch<SecurityProfile>(`/v1/security-profiles/${id}`, input, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['security-profiles', workspaceId] });
    },
  });
}

export function useDeleteSecurityProfile() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return api.delete(`/v1/security-profiles/${id}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['security-profiles', workspaceId] });
    },
  });
}

export function useAssignSecurityProfile() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, profileId }: { userId: string; profileId: string | null }) => {
      const token = await getToken();
      return api.patch(`/v1/security-profiles/assign/${userId}`, { profileId }, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['security-profiles', workspaceId] });
      void queryClient.invalidateQueries({ queryKey: ['members', workspaceId] });
    },
  });
}
